"use client";

import Image from "next/image";
import React, { useRef, useState, useCallback, useEffect } from "react";
import { useController, useFormContext } from "react-hook-form";

interface FileType {
  type: string;
  extensions: string[];
  label: string;
}

interface FileInputProps {
  name: string; // Field name for react-hook-form
  className?: string;
  acceptedFileTypes?: FileType[]; // Configurable file types
  maxFileSize?: number; // Max file size in MB
  dragDropText?: string; // Custom drag drop text
  dragDropSubtext?: string; // Custom drag drop subtext
  label?: string; // Optional label
  placeholder?: string; // Optional placeholder text
  error?: string; // Error message
  disabled?: boolean; // Disable the input
  existingFileUrl?: string;
  comments?: string;
}

const FileInput = ({
  name,
  className = "",
  acceptedFileTypes = [
    {
      type: "video",
      extensions: [".mp4", ".mpeg", ".webm", ".avi", ".mov"],
      label: "Video files",
    },
    {
      type: "image",
      extensions: [".jpg", ".jpeg", ".png", ".gif", ".webp"],
      label: "Image files",
    },
    {
      type: "document",
      extensions: [".pdf", ".doc", ".docx", ".txt"],
      label: "Document files",
    },
  ],
  maxFileSize = 50, // 50MB default
  dragDropText = "Drop your files here",
  dragDropSubtext = "or click to browse",
  label,
  error,
  disabled = false,
  existingFileUrl,
  comments,
}: FileInputProps) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [hasExistingFile, setHasExistingFile] = useState(false);

  // Get control from form context
  const { control } = useFormContext();

  // Use useController to manage the field
  const {
    field: { value: formValue, onChange },
    fieldState: { error: fieldError },
  } = useController({
    name,
    control,
  });

  // Verificar si tenemos un archivo existente del backend
  useEffect(() => {
    if (existingFileUrl || (typeof formValue === "string" && formValue)) {
      setHasExistingFile(true);
    } else {
      setHasExistingFile(false);
    }
  }, [existingFileUrl, formValue]);

  // Obtener el nombre del archivo actual para mostrar
  const getCurrentFileName = () => {
    if (selectedFile) {
      return selectedFile.name;
    }
    if (existingFileUrl) {
      // Extraer nombre del archivo de la URL
      try {
        const url = new URL(existingFileUrl);
        const pathParts = url.pathname.split("/");
        const fileName = pathParts[pathParts.length - 1];
        // Decodificar caracteres especiales en la URL
        return decodeURIComponent(fileName) || "Archivo cargado";
      } catch {
        // Si no es una URL válida, tratar como path
        const pathParts = existingFileUrl.split("/");
        const fileName = pathParts[pathParts.length - 1];
        return fileName || "Archivo cargado";
      }
    }
    if (typeof formValue === "string" && formValue) {
      // Extraer nombre del archivo de la URL o path
      const urlParts = formValue.split("/");
      return urlParts[urlParts.length - 1] || "Archivo cargado";
    }
    if (formValue instanceof File) {
      return formValue.name;
    }
    return "Archivo cargado";
  };

  // Obtener el tamaño del archivo actual para mostrar
  const getCurrentFileSize = () => {
    if (selectedFile) {
      return getFileSize(selectedFile.size);
    }
    if (formValue instanceof File) {
      return getFileSize(formValue.size);
    }
    return null; // No se puede determinar el tamaño para archivos existentes
  };

  // Obtener el tipo de archivo actual para el icono
  const getCurrentFileType = () => {
    if (selectedFile) {
      return selectedFile.type;
    }
    if (formValue instanceof File) {
      return formValue.type;
    }
    // Para archivos existentes, intentar determinar el tipo por extensión
    const fileName = getCurrentFileName();
    const extension = fileName.split(".").pop()?.toLowerCase();
    if (extension) {
      if (["jpg", "jpeg", "png", "gif", "webp"].includes(extension)) {
        return "image/" + extension;
      }
      if (["mp4", "mpeg", "webm", "avi", "mov"].includes(extension)) {
        return "video/" + extension;
      }
      if (["pdf"].includes(extension)) {
        return "application/pdf";
      }
    }
    return "application/octet-stream";
  };

  // Validar tipo y tamaño del archivo
  const validateFile = useCallback(
    (file: File): { isValid: boolean; error?: string } => {
      // Verificar tamaño del archivo
      if (file.size > maxFileSize * 1024 * 1024) {
        return {
          isValid: false,
          error: `El archivo debe ser menor a ${maxFileSize}MB`,
        };
      }

      // Verificar tipo de archivo
      const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
      const isValidType = acceptedFileTypes.some(
        (fileType) =>
          fileType.extensions.includes(fileExtension) ||
          file.type.startsWith(fileType.type + "/")
      );

      if (!isValidType) {
        return {
          isValid: false,
          error: `Tipo de archivo inválido. Tipos aceptados: ${acceptedFileTypes
            .map((ft) => ft.label)
            .join(", ")}`,
        };
      }

      return { isValid: true };
    },
    [acceptedFileTypes, maxFileSize]
  );

  // Procesar archivo subido
  const processFile = useCallback(
    (file: File) => {
      const validation = validateFile(file);
      if (!validation.isValid) {
        setValidationError(validation.error || "Archivo inválido");
        return false;
      }

      setValidationError(null);
      setSelectedFile(file);
      setHasExistingFile(false);

      // Crear URL de preview para imágenes y videos
      if (file.type.startsWith("image/") || file.type.startsWith("video/")) {
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
      } else {
        setPreviewUrl(null);
      }

      // Actualizar react-hook-form
      onChange(file);
      return true;
    },
    [validateFile, onChange]
  );

  // Limpiar archivo actual
  const clearFile = useCallback(() => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedFile(null);
    setPreviewUrl(null);
    setValidationError(null);
    setHasExistingFile(false);
    onChange(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [previewUrl, onChange]);

  // Manejadores de drag and drop
  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        setIsDragOver(true);
      }
    },
    [disabled]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      if (disabled) return;

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        processFile(files[0]);
      }
    },
    [processFile, disabled]
  );

  // Manejador del input de archivo
  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        processFile(file);
      }
    },
    [processFile]
  );

  // Generar string de tipos de archivo aceptados para el input
  const getAcceptedTypes = () => {
    const mimeTypes: string[] = [];

    acceptedFileTypes.forEach((fileType) => {
      switch (fileType.type) {
        case "video":
          mimeTypes.push(
            "video/mp4",
            "video/mpeg",
            "video/webm",
            "video/avi",
            "video/quicktime"
          );
          break;
        case "image":
          mimeTypes.push(
            "image/jpeg",
            "image/jpg",
            "image/png",
            "image/gif",
            "image/webp"
          );
          break;
        case "document":
          mimeTypes.push(
            "application/pdf",
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            "text/plain"
          );
          break;
        case "audio":
          mimeTypes.push("audio/mpeg", "audio/wav", "audio/ogg", "audio/mp3");
          break;
        default:
          // Agregar extensiones específicas como fallback
          fileType.extensions.forEach((ext) => {
            switch (ext.toLowerCase()) {
              case ".pdf":
                mimeTypes.push("application/pdf");
                break;
              case ".doc":
                mimeTypes.push("application/msword");
                break;
              case ".docx":
                mimeTypes.push(
                  "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                );
                break;
              case ".txt":
                mimeTypes.push("text/plain");
                break;
              case ".mp4":
                mimeTypes.push("video/mp4");
                break;
              case ".webm":
                mimeTypes.push("video/webm");
                break;
              case ".jpg":
              case ".jpeg":
                mimeTypes.push("image/jpeg");
                break;
              case ".png":
                mimeTypes.push("image/png");
                break;
              case ".gif":
                mimeTypes.push("image/gif");
                break;
              case ".webp":
                mimeTypes.push("image/webp");
                break;
            }
          });
      }
    });

    // Remover duplicados y unir
    return [...new Set(mimeTypes)].join(",");
  };

  // Generar texto de leyenda
  const getLegendText = () => {
    const extensions = acceptedFileTypes.flatMap((ft) => ft.extensions);
    return `Formatos aceptados: ${extensions.join(
      ", "
    )} (Máx ${maxFileSize}MB)`;
  };

  // Obtener icono del archivo basado en el tipo
  const getFileIcon = () => {
    const fileType = getCurrentFileType();

    if (fileType.startsWith("image/")) return "🖼️";
    if (fileType.startsWith("video/")) return "🎬";
    if (fileType.startsWith("audio/")) return "🎵";
    if (fileType.includes("pdf")) return "📄";
    if (fileType.includes("document") || fileType.includes("word")) return "📝";
    return "📄";
  };

  // Obtener tamaño del archivo en formato legible
  const getFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const currentError = error || fieldError?.message || validationError;
  const hasFile = selectedFile || hasExistingFile || formValue;

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label className="block mb-2 text-sm font-semibold text-gray-600">
          {label}
        </label>
      )}

      <div className="space-y-3">
        {/* Preview del Archivo */}
        {hasFile && (
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="text-2xl">{getFileIcon()}</div>
                <div>
                  <p className="font-medium text-sm text-gray-900">
                    {getCurrentFileName()}
                  </p>
                  {getCurrentFileSize() && (
                    <p className="text-xs text-gray-500">
                      {getCurrentFileSize()}
                    </p>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={clearFile}
                className="text-red-500 hover:text-red-700 text-sm font-medium"
                disabled={disabled}
              >
                Eliminar
              </button>
            </div>

            {/* Mostrar comentarios del backend si existen */}
            {comments && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <div className="flex items-start space-x-2">
                  <div className="text-blue-600 text-sm">💬</div>
                  <div className="flex-1">
                    <p className="text-sm text-blue-800 font-medium mb-1">
                      Comentarios del sistema:
                    </p>
                    <p className="text-xs text-blue-700 leading-relaxed">
                      {comments}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Preview de Imagen/Video */}
            {previewUrl && (
              <div className="mt-3">
                {selectedFile?.type.startsWith("image/") ? (
                  <Image
                    src={previewUrl}
                    alt="Preview"
                    width={0}
                    height={0}
                    sizes="100vw"
                    className="max-w-full h-32 object-cover rounded border"
                  />
                ) : selectedFile?.type.startsWith("video/") ? (
                  <video
                    src={previewUrl}
                    controls
                    className="max-w-full h-32 object-cover rounded border"
                  />
                ) : null}
              </div>
            )}

            {/* Preview de archivo existente (si es una imagen) */}
            {!previewUrl &&
              hasExistingFile &&
              getCurrentFileType().startsWith("image/") && (
                <div className="mt-3">
                  <Image
                    src={
                      existingFileUrl ||
                      (typeof formValue === "string" ? formValue : "")
                    }
                    alt="Archivo existente"
                    width={0}
                    height={0}
                    sizes="100vw"
                    className="max-w-full h-32 object-cover rounded border"
                  />
                </div>
              )}
          </div>
        )}

        {/* Área de Drag and Drop */}
        {!hasFile && (
          <label className="block w-full">
            <input
              ref={fileInputRef}
              type="file"
              accept={getAcceptedTypes()}
              onChange={handleFileInput}
              className="hidden"
              disabled={disabled}
            />
            <div
              className={`border-2 border-dashed rounded-lg py-6 text-center cursor-pointer transition-colors duration-200 ${
                disabled
                  ? "border-gray-200 bg-gray-50 cursor-not-allowed"
                  : isDragOver
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="flex flex-col items-center">
                {/* Icono de Upload */}
                <div className="w-12 h-12 flex items-center justify-center mb-3">
                  <svg
                    className={`w-8 h-8 ${
                      disabled ? "text-gray-300" : "text-gray-400"
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                </div>

                {/* Texto de Call to Action */}
                <div
                  className={`${disabled ? "text-gray-400" : "text-gray-500"}`}
                >
                  <p className="font-medium">{dragDropText}</p>
                  <p className="text-xs mt-1">{dragDropSubtext}</p>
                </div>
              </div>
            </div>
          </label>
        )}

        {/* Leyenda */}
        <p className="text-xs text-gray-500 text-center">{getLegendText()}</p>

        {/* Mostrar Errores */}
        {currentError && (
          <p className="text-red-500 text-sm text-center">{currentError}</p>
        )}
      </div>
    </div>
  );
};

export default FileInput;
